import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import apiClient from '../utils/apiClient';

const AcknowledgmentPage = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [tokenData, setTokenData] = useState(null);
  const [status, setStatus] = useState('pending');
  const [message, setMessage] = useState('');

  const token = searchParams.get('token');

  useEffect(() => {
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setTokenData(payload);
      } catch (err) {
        setStatus('error');
        setMessage('Invalid acknowledgment link.');
      }
    } else {
      setStatus('error');
      setMessage('Missing acknowledgment token.');
    }
  }, [token]);

  const handleConfirmAcknowledgment = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://meal.7edge.io'}/acknowledge?token=${token}`);
      const data = await response.text();
      
      if (response.ok) {
        if (data.includes('already been acknowledged')) {
          setStatus('already_acknowledged');
          setMessage(data);
        } else {
          setStatus('success');
          setMessage(data || 'Acknowledgment successful');
        }
      } else {
        setStatus('error');
        setMessage(data || 'Failed to acknowledge requests');
      }
    } catch (error) {
      setStatus('error');
      setMessage('Failed to acknowledge requests');
    } finally {
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const renderContent = () => {
    if (status === 'success') {
      return (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Card sx={{
            textAlign: 'center',
            p: 6,
            background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)',
            border: '2px solid #4caf50',
            borderRadius: 4
          }}>
            <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" fontWeight={800} gutterBottom sx={{
              background: 'linear-gradient(45deg, #2e7d32 30%, #4caf50 90%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              ✅ Successfully Acknowledged
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
              {message}
            </Typography>
          </Card>
        </motion.div>
      );
    }

    if (status === 'already_acknowledged') {
      return (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Card sx={{
            textAlign: 'center',
            p: 6,
            background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
            border: '2px solid #ff9800',
            borderRadius: 4
          }}>
            <InfoIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
            <Typography variant="h4" fontWeight={800} gutterBottom sx={{
              background: 'linear-gradient(45deg, #f57c00 30%, #ff9800 90%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              ℹ️ Already Acknowledged
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
              {message}
            </Typography>
          </Card>
        </motion.div>
      );
    }

    if (status === 'error') {
      return (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Card sx={{
            textAlign: 'center',
            p: 6,
            background: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)',
            border: '2px solid #f44336',
            borderRadius: 4
          }}>
            <WarningIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
            <Typography variant="h4" fontWeight={800} gutterBottom sx={{
              background: 'linear-gradient(45deg, #c62828 30%, #f44336 90%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              ⚠️ Error
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
              {message}
            </Typography>
          </Card>
        </motion.div>
      );
    }

    return (
      <motion.div variants={itemVariants}>
        <Card sx={{
          p: 4,
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          border: '1px solid #e2e8f0',
          borderRadius: 4
        }}>
          <Box sx={{
            textAlign: 'center',
            mb: 4,
            p: 3,
            background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
            borderRadius: 3,
            color: 'white'
          }}>
            <Typography variant="h4" fontWeight={800} sx={{
              background: 'linear-gradient(45deg, #fff 30%, #ffe0b2 90%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1
            }}>
              Food Request Acknowledgment
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Please review and confirm the acknowledgment details below
            </Typography>
          </Box>

          {tokenData && (
            <Card variant="outlined" sx={{
              mb: 4,
              background: 'linear-gradient(135deg, rgba(32, 213, 213, 0.05) 0%, rgba(6, 214, 160, 0.05) 100%)',
              border: '2px solid rgba(32, 213, 213, 0.2)',
              borderRadius: 3
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: '#2c3e50', mb: 3 }}>
                  Acknowledgment Details
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" fontWeight={600} sx={{ color: '#64748b' }}>Date:</Typography>
                    <Chip label={tokenData.date} sx={{
                      backgroundColor: '#f5f5f5',
                      color: '#333',
                      fontWeight: 600
                    }} />
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" fontWeight={600} sx={{ color: '#64748b' }}>Total Requests:</Typography>
                    <Chip label={`${tokenData.requestCount} request(s)`} sx={{
                      backgroundColor: '#f5f5f5',
                      color: '#333',
                      fontWeight: 600
                    }} />
                  </Box>
                </Box>
                
                <Typography variant="body2" sx={{ mt: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 2, color: '#555' }}>
                  All food requests for {tokenData.date} will be acknowledged.
                </Typography>
              </CardContent>
            </Card>
          )}

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                variant="contained"
                onClick={handleConfirmAcknowledgment}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
                sx={{
                  minWidth: 200,
                  background: 'linear-gradient(135deg, #06d6a0 0%, #20d5d5 100%)',
                  color: 'white',
                  fontWeight: 700,
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  boxShadow: '0 4px 15px rgba(6, 214, 160, 0.3)',
                  '&:hover': {
                    boxShadow: '0 6px 20px rgba(6, 214, 160, 0.4)'
                  },
                  '&:disabled': {
                    background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                    color: '#94a3b8'
                  }
                }}
              >
                {loading ? 'Processing...' : '✓ Confirm Acknowledgment'}
              </Button>
            </motion.div>
            
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                variant="outlined"
                onClick={() => window.close()}
                disabled={loading}
                sx={{
                  borderColor: '#6c757d',
                  color: '#6c757d',
                  fontWeight: 700,
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  '&:hover': {
                    borderColor: '#6c757d',
                    backgroundColor: 'rgba(108, 117, 125, 0.1)'
                  }
                }}
              >
                Cancel
              </Button>
            </motion.div>
          </Box>
        </Card>
      </motion.div>
    );
  };

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            borderRadius: 4,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid #e2e8f0'
          }}
        >
          <AnimatePresence mode="wait">
            {renderContent()}
          </AnimatePresence>
        </Paper>
      </motion.div>
    </Container>
  );
};

export default AcknowledgmentPage;